// src/api/index.js
import axios from 'axios';
import { createAppTransport } from '@1823-partners/core';
import { guardWrite } from '../config/backendFeatures';

// Same-origin /api/recon: the SWA Function (api/recon) when deployed, the CRA
// proxy (src/setupProxy.js) to a local pam-deacon dev_server under npm start.
const api = createAppTransport({ axios, appName: 'recon' });

export const fetchReconEntry = async (acctId, cust, srcAcct, secId, custId) => {
  return api.get(`/recon-entry?acctId=${acctId}&cust=${cust}&srcAcct=${srcAcct}&secId=${secId}&custId=${custId}`);
};

export const fetchReconEntries = async (asofdt) => {
  const url = asofdt ? `/recon-entries?asofdt=${asofdt}` : `/recon-entries`;
  return api.get(url);
};

export const fetchReconDates = async () => {
  return api.get('/recon-dates');
};

export const saveReconEntries = async (entries) =>
  guardWrite('comments', () => api.post('/save-entries', entries));

// ── Daily Clearwater positions (Fixed Income → Weekly / Daily) ─────────────
// The month-end recon above reads /recon-entries; these read the daily
// Clearwater position table, which is the only side of the recon that exists
// between month-ends.

export const fetchDailyPositionDates = async () => {
  return api.get('/daily-position-dates');
};

/**
 * One day's positions, compared against *compareTo*.
 *
 * compareTo is always sent when known: the Weekly perspective compares a week
 * close against the PRIOR WEEK's close, which is not the previous business day,
 * so letting the server guess would answer a different question.
 */
export const NO_BASELINE = 'none';

export const fetchDailyPositions = async (asofdt, compareTo) => {
  // Always explicit. Omitting compareTo asks the server to resolve the previous
  // available DAY, which is wrong for the oldest week: it would compare that
  // week's Friday against its own Thursday while the header says there is no
  // baseline. `none` says what the caller means.
  const params = new URLSearchParams({ asofdt, compareTo: compareTo || NO_BASELINE });
  return api.get(`/daily-positions?${params.toString()}`);
};

export const fetchExcludedPositions = async () => {
  return api.get('/excluded-positions');
};

export const saveExclusions = async (exclusions) =>
  guardWrite('exclusions', () => api.post('/save-exclusions', exclusions));

export const deleteExclusions = async (exclusions) =>
  guardWrite('exclusions', () => api.post('/delete-exclusions', exclusions));

export const fetchSecurityXref = async () => {
  return api.get('/security-xref');
};

// Crosswalk rows go through pam-deacon's checked writer: each row carries the
// MODIFIED_DATE it was loaded with, unchanged (null for a new mapping). The
// response carries the new `modified_date`; a 409 means someone saved first.
export const saveSecurityXref = async (rows) => api.post('/save-xref', rows);

export const deleteSecurityXref = async (rows) => api.post('/delete-xref', rows);

// ── Reference positions (Derivative Recon → Reference Positions tab) ────────
// These four resolve to the payload directly rather than the axios response,
// because the view loads all of them together with Promise.all.

const qs = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
};

export const fetchPositionsDates = async () =>
  (await api.get('/positions/dates')).data;

export const fetchPositions = async (params) =>
  (await api.get(`/positions${qs(params)}`)).data;

export const fetchPositionsSummary = async (params) =>
  (await api.get(`/positions/summary${qs(params)}`)).data;

export const fetchPositionsByIssuer = async (params) =>
  (await api.get(`/positions/by-issuer${qs(params)}`)).data;

export const fetchPositionsByCurrency = async (params) =>
  (await api.get(`/positions/by-currency${qs(params)}`)).data;

export default api;
